import * as React from "react";

import { Loader, Container, Header, Grid, Divider, Modal } from "semantic-ui-react";

import moment = require("moment");

import { PrintDialog } from "@teamprevent/common/components/PrintDialog";
import { AllImmsCompletedForRoleCertificateModel } from "@teamprevent/common/ppq/models";

import { EmployeeDetail } from "./EmployeeDetail";
import { LegalAddress } from "./LegalAddress";

interface Props {
    loadCertificateDetails: () => Promise<AllImmsCompletedForRoleCertificateModel>;
}

const AllImmsCompletedForRoleCertificate: React.FunctionComponent<Props> = (props) => {
    const [certificateDetails, setCertificateDetails] = React.useState<AllImmsCompletedForRoleCertificateModel>();
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            setCertificateDetails(await props.loadCertificateDetails());
            setLoading(false);
        })();
    }, []);

    if (loading) {
        return (
            <Modal basic open={loading}>
                <Modal.Content>
                    <Loader active={loading} size='large'>Loading</Loader>
                </Modal.Content>
            </Modal>
        );
    }

    if (!certificateDetails) {
        return null;
    }

    const { employeeDetails, immunisationClearDate, outcome, clinicalComments, completedByName } = certificateDetails;

    return (
        <Container className="immunisation-clear-certificate">
            <Grid container>
                <Grid.Row className="report-title-row">
                    <Grid.Column>
                        <Header as="h1">Full Immunisation Clearance for Role (Medical in confidence)</Header>
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            <Grid container className="details-grid">
                <Grid.Row className="report-section-row">
                    <Grid.Column>
                        <Header as="h4">Details</Header>
                    </Grid.Column>
                </Grid.Row>
                <EmployeeDetail
                    label="Name"
                    value={`${employeeDetails.title} ${employeeDetails.firstName} ${employeeDetails.lastName}`}
                />
                {employeeDetails.jobTitle && (
                    <EmployeeDetail
                        label="Job Title"
                        value={employeeDetails.jobTitle}
                    />
                )}
                <EmployeeDetail
                    label="DOB"
                    value={employeeDetails.dateOfBirth?.format("DD/MM/YYYY")}
                />
                <EmployeeDetail
                    label="Customer Name"
                    value={`${employeeDetails.customerName}`}
                />
                {employeeDetails.orgLevel1 && (
                    <EmployeeDetail
                        label="Organisation Level One"
                        value={employeeDetails.orgLevel1}
                    />
                )}
                {employeeDetails.orgLevel2 && (
                    <EmployeeDetail
                        label="Organisation Level Two"
                        value={employeeDetails.orgLevel2}
                    />
                )}
                {employeeDetails.orgLevel3 && (
                    <EmployeeDetail
                        label="Organisation Level Three"
                        value={employeeDetails.orgLevel3}
                    />
                )}
                {employeeDetails.orgLevel4 && (
                    <EmployeeDetail
                        label="Organisation Level Four"
                        value={employeeDetails.orgLevel4}
                    />
                )}
                <EmployeeDetail
                    label="Date of Assessment"
                    value={immunisationClearDate.format("DD/MM/YYYY")}
                />
            </Grid>

            <Grid container>
                <Grid.Row className="report-section-row">
                    <Grid.Column>
                        <Header as="h4">Outcome of Immunisation</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    {outcome}
                </Grid.Row>
            </Grid>
            <Divider />
            <Grid container>
                <Grid.Row className="no-flex">
                    <h4>Clinical Comments:</h4>
                    {clinicalComments}
                </Grid.Row>
            </Grid>

            <Grid container className="details-grid">
                <Grid.Row >
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Name
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {completedByName}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Date Report Produced
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {moment().format("DD/MM/YYYY")}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Address
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        <LegalAddress immsCompletionDate={immunisationClearDate.toDate()} />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Telephone
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        <p>Tel: {employeeDetails.customerNumber}</p>
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            <Grid container>
                <Grid.Row>
                    * Where applicable, Clearance for Exposure Prone Procedure (EPP) will clearly be stated in the Outcome of
                    Assessment, if EPP clearance IS NOT explicitly stated in the outcome then this employee HAS NOT not
                    been cleared for this type of work.
                </Grid.Row>
            </Grid>
            <Divider section />
            <Container>
                <PrintDialog />
            </Container>
        </Container >
    );
};

export { AllImmsCompletedForRoleCertificate };
